
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface NavbarProps {
  currentView: 'home' | 'projects';
  setView: (view: 'home' | 'projects') => void;
}

const Navbar: React.FC<NavbarProps> = ({ currentView, setView }) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => isOpen && setIsOpen(false);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isOpen]);

  const toggleMenu = () => setIsOpen(!isOpen);

  const scrollWithOffset = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const navLinks = [
    { name: 'Inicio', id: 'home', action: () => setView('home') },
    { name: 'Proyectos', id: 'projects', action: () => setView('projects') },
    { name: 'Contacto', id: 'contact', action: () => { 
        if (currentView !== 'home') {
          setView('home');
          setTimeout(() => {
            scrollWithOffset('contact-form');
          }, 600);
        } else {
          scrollWithOffset('contact-form');
        }
      } 
    },
  ];

  const handleLinkClick = (e: React.MouseEvent, action: () => void) => {
    e.preventDefault();
    action();
    setIsOpen(false);
  };

  return (
    <>
      <motion.nav 
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="fixed top-6 left-0 right-0 z-[100] px-6 md:px-12 flex items-center justify-end lg:justify-center pointer-events-none"
      >
        {/* Desktop Nav Pill: Centered in the screen */}
        <div className="hidden lg:flex items-center glass-panel px-2 py-2 rounded-full border border-white/5 pointer-events-auto shadow-2xl">
          {/* Logo integrated in the pill */}
          <button 
            onClick={() => setView('home')}
            className="w-9 h-9 rounded-full bg-[#39FF14] flex items-center justify-center font-bold text-black shadow-[0_0_10px_#39FF14] text-[10px] hover:scale-105 transition-transform cursor-pointer mr-6 ml-1"
          >
            JT
          </button>
          
          <div className="flex items-center gap-8 pr-8">
            {navLinks.map((link) => (
              <button 
                key={link.id} 
                onClick={(e) => handleLinkClick(e, link.action)}
                className={`text-[10px] font-bold uppercase tracking-[0.2em] transition-all duration-300 relative group ${currentView === link.id ? 'text-[#39FF14]' : 'text-gray-300 hover:text-[#39FF14]'}`}
              >
                {link.name}
                <span className={`absolute -bottom-1 left-0 h-[1px] bg-[#39FF14] transition-all duration-300 ${currentView === link.id ? 'w-full' : 'w-0 group-hover:w-full'}`} />
              </button>
            ))}
          </div>
        </div>

        {/* Mobile/Tablet Pill: Logo + Hamburger integrated */}
        <div className="lg:hidden flex items-center gap-2 glass-panel p-1.5 pr-2 rounded-full border border-white/10 pointer-events-auto shadow-2xl">
          <button 
            onClick={() => { setView('home'); setIsOpen(false); }}
            className="w-10 h-10 rounded-full bg-[#39FF14] flex items-center justify-center font-bold text-black shadow-[0_0_15px_#39FF14] text-[10px] hover:scale-105 transition-transform cursor-pointer"
          >
            JT
          </button>
          
          <button 
            onClick={toggleMenu}
            className="w-10 h-10 flex flex-col items-center justify-center gap-1.5 focus:outline-none group relative"
            aria-label="Menu"
          >
            <motion.span 
              animate={isOpen ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 }}
              className="w-5 h-0.5 bg-white group-hover:bg-[#39FF14] transition-colors"
            />
            <motion.span 
              animate={isOpen ? { opacity: 0, x: 20 } : { opacity: 1, x: 0 }}
              className="w-5 h-0.5 bg-white group-hover:bg-[#39FF14] transition-colors"
            />
            <motion.span 
              animate={isOpen ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 }}
              className="w-5 h-0.5 bg-white group-hover:bg-[#39FF14] transition-colors"
            />
          </button>
        </div>
      </motion.nav>

      {/* Overlay Menu */}
      <AnimatePresence mode="wait">
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-0 z-[90] lg:hidden bg-[#121212]/95 backdrop-blur-2xl flex flex-col items-center justify-center border-l border-[#39FF14]/20"
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60%] h-[60%] bg-[#39FF14]/5 blur-[150px] rounded-full pointer-events-none" />

            <div className="flex flex-col items-center gap-10 z-10">
              {navLinks.map((link, index) => (
                <motion.button
                  key={link.id}
                  onClick={(e) => handleLinkClick(e, link.action)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className="group relative"
                >
                  <span className={`font-syncopate text-3xl md:text-5xl font-bold uppercase tracking-tighter transition-colors duration-300 ${currentView === link.id ? 'text-[#39FF14]' : 'text-white hover:text-[#39FF14]'}`}>
                    {link.name}
                  </span>
                  <span className={`absolute -bottom-2 left-0 h-1 bg-[#39FF14] shadow-[0_0_10px_#39FF14] transition-all duration-300 ${currentView === link.id ? 'w-full' : 'w-0 group-hover:w-full'}`} />
                </motion.button>
              ))}
            </div>
            
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="absolute bottom-12 flex flex-col items-center gap-4"
            >
              <div className="flex gap-8 text-gray-500 font-bold text-[10px] tracking-[0.3em] uppercase">
                <a href="https://www.linkedin.com/in/juanmanueltenaluque" target="_blank" rel="noopener noreferrer" className="hover:text-[#39FF14] transition-colors">LinkedIn</a>
                <a href="#" className="hover:text-[#39FF14] transition-colors">GitHub</a>
              </div>
              <div className="text-[9px] text-[#39FF14]/40 font-bold uppercase tracking-widest text-center">
                juanma tena luque &bull; fullstack dev
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
